import {
	Badge,
	createStyles,
	Divider,
	List,
	makeStyles,
	withStyles,
} from "@material-ui/core";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import SubMenuIcon from "@material-ui/icons/ChevronRight";
import LinkIcon from "@material-ui/icons/Link";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import SportsSoccerIcon from "@material-ui/icons/SportsSoccer";
import { get } from "lodash";
import { usePermissions } from "ra-core";
import React, { createElement, useState } from "react";
import {
	getResources,
	MenuItemLink,
	Responsive,
	translate,
	useGetOne,
	useLocale,
	useQueryWithStore,
} from "react-admin";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import ContactMenu from "./ContactMenu";
import SubMenu from "./SubMenu";

const GROUPS_ORDER = {
	affiliation: 1,
	reports: 5,
	dashboard: 5,
	bookmakers: 10,
	gamification: 20,
	bonus_comparator: 30,
	pages: 50,
	admin: 100,
};
const GROUPS_VISIBILITY = {
	admin: false,
	bookmakers: true,
	reports: true,
	affiliation: true,
	bonus_comparator: true,
	dashboard: true,
	pages: false,
	gamification: true,
};
const BOOKMAKER_TYPE_ICONS = {
	sport: SportsSoccerIcon,
	casino: MonetizationOnIcon,
};

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		maxWidth: 360,
		backgroundColor: theme.palette.background.paper,
		marginRight: theme.spacing(1),
		paddingTop: theme.spacing(1),
		boxShadow: theme.shadows[24],
		[theme.breakpoints.up("sm")]: {
			height: "100%",
		},
		[theme.breakpoints.down("md")]: {
			paddingBottom: "6em",
		},
	},
	nested: {
		paddingLeft: theme.spacing(4),
	},
	bonusComparatorListItem: {
		color: theme.palette.primary.main,
		fontWeight: "bold",
	},
}));

const mapGroups = (resources, permissions) => {
	let groups = []
		.concat(resources.filter((r) => r.hasList && r.options && r.icon))
		.filter(
			(item) =>
				permissions &&
				(item.options.roles === undefined ||
					item.options.roles.filter((role) => permissions(role)).length > 0) &&
				(!item.options.excludeRoles ||
					item.options.excludeRoles.filter((role) => permissions(role)).length === 0)
		)
		.reduce((groups, resource) => {
			if (resource.name === "deals") {
				return groups;
			}
			let groupName = resource.options ? resource.options.group : "";
			let group = groups.find((g) => g.name === groupName);
			if (group) {
				group.resources.push(resource);
				group.resources.sort((a, b) =>
					a.options.order > b.options.order
						? 1
						: a.options.order < b.options.order
							? -1
							: 0
				);
			} else {
				group = {
					name: groupName,
					order: GROUPS_ORDER[groupName] || 1000,
					resources: [resource],
				};
				groups.push(group);
			}
			return groups;
		}, []);

	groups.sort((a, b) => (a.order > b.order ? 1 : a.order < b.order ? -1 : 0));
	return groups;
};

const isGroupSelected = (location, group) =>
	group.resources &&
	group.resources.some(
		(resource) =>
			location.pathname === `/${resource.name}` ||
			location.pathname === resource.path ||
			location.pathname.indexOf(`/${resource.name}?`) === 0 ||
			location.pathname.indexOf(`/${resource.name}/`) === 0
	);
const isResourceSelected = (location, resource) =>
	location.pathname === `/${resource.name}` ||
	location.pathname === resource.path ||
	location.pathname.indexOf(`/${resource.name}?`) === 0 ||
	(location.pathname.indexOf(`/${resource.name}/`) === 0 &&
		!location.pathname.endsWith("/create"));

const styles = (theme) =>
	createStyles({
		version: {
			padding: theme.spacing(1),
		},
	});
const SafeBadge = ({ titleAccess, children, ...props }) => (
	<Badge {...props}>{children}</Badge>
);

const Menu = ({
	resources,
	onMenuClick,
	logout,
	open,
	classes,
	translate,
	location,
	hasDashboard,
}) => {
	const { data: userProfile } = useGetOne("users", "profile");
	const { permissions } = usePermissions();
	const [visibility, setVisibility] = useState({ ...GROUPS_VISIBILITY });
	const locale = useLocale();
	const customClasses = useStyles();
	const { loaded: loadedBookmakerTypes, data: bookmakerTypes } =
		useQueryWithStore({
			type: "getList",
			resource: "bookmaker-types",
			payload: {
				sort: { field: "order", order: "desc" },
				pagination: {
					page: 1,
					perPage: 10,
				},
			},
		});

	const { loaded: loadedCms, data: pages } = useQueryWithStore({
		type: "getList",
		resource: `pages/menu`,
		payload: {
			filter: {
				language: locale,
			},
			sort: { field: "order", order: "desc" },
			pagination: {
				page: 1,
				perPage: 10,
			},
		},
	});

	const { data: badges } = useQueryWithStore({
		type: "getList",
		resource: `users/badges`,
		payload: {
			sort: { field: "order", order: "desc" },
			pagination: {
				page: 1,
				perPage: 100,
			},
		},
	});

	return (
		<List component="nav" className={customClasses.root}>
			{loadedBookmakerTypes &&
				!!bookmakerTypes &&
				permissions &&
				permissions("user") &&
				!permissions("affiliate_manager") && (
					<SubMenu
						key="affiliation"
						handleToggle={() =>
							setVisibility({
								...visibility,
								affiliation: visibility.affiliation !== true,
							})
						}
						isOpen={
							visibility.affiliation || isGroupSelected(location, "affiliation")
						}
						sidebarIsOpen={open}
						name={`menu.groups.affiliation`}
						icon={<SubMenuIcon />}
					>
						{bookmakerTypes.map((item) => (
							<MenuItemLink
								key={`deals-${item.id}`}
								to={`/deals/${item.id}`}
								primaryText={item.name}
								isActive={(match, location) =>
									isResourceSelected(location, {
										name: `deals/${item.id}`,
										path: `/deals/${item.id}`,
									})
								}
								leftIcon={createElement(
									BOOKMAKER_TYPE_ICONS[item.icon] || BookmarkIcon
								)}
								onClick={onMenuClick}
							/>
						))}

						<MenuItemLink
							to={`/channels`}
							primaryText={translate("menu.items.channels")}
							isActive={(match, location) =>
								isResourceSelected(location, {
									name: "/channels",
									path: "/channels",
								})
							}
							onClick={onMenuClick}
							leftIcon={createElement(LinkIcon)}
						/>
					</SubMenu>
				)}
			{loadedBookmakerTypes &&
				!!bookmakerTypes &&
				permissions &&
				permissions("user") &&
				!permissions("affiliate_manager") && (
					<SubMenu
						key="bonus_comparator"
						handleToggle={() =>
							setVisibility({
								...visibility,
								bonus_comparator: visibility.bonus_comparator !== true,
							})
						}
						isOpen={
							visibility.bonus_comparator ||
							isGroupSelected(location, "bonus_comparator")
						}
						sidebarIsOpen={open}
						name={`menu.groups.bonus_comparator`}
						icon={<SubMenuIcon />}
						classes={{
							listItem: customClasses.bonusComparatorListItem,
						}}
					>
						{bookmakerTypes.map((item) => {
							let to = `/bonus-comparators?filter=${encodeURIComponent(
								JSON.stringify({
									bookmaker_type_id: item.id,
								})
							)}`;
							return (
								<MenuItemLink
									key={`bonus-comparator-${item.id}`}
									to={to}
									primaryText={item.name}
									isActive={(match, location) =>
										isResourceSelected(location, {
											name: `bonus-comparators`,
											path: to,
										})
									}
									leftIcon={createElement(
										BOOKMAKER_TYPE_ICONS[item.icon] || BookmarkIcon
									)}
									onClick={onMenuClick}
								/>
							);
						})}
					</SubMenu>
				)}
			{mapGroups(resources, permissions, hasDashboard).map((group) => (
				<SubMenu
					key={group.name}
					handleToggle={() =>
						setVisibility({
							...visibility,
							[group.name]: visibility[group.name] !== true,
						})
					}
					isOpen={visibility[group.name] || isGroupSelected(location, group)}
					sidebarIsOpen={open}
					name={`menu.groups.${group.name}`}
					icon={<SubMenuIcon />}
				>
					{group.resources.map((resource) => {
						let to = `${resource.path || `/${resource.name}`}`;
						if (resource.name === "user-wtrs") {
							if (
								!get(userProfile, "profile.has_wtrs") &&
								permissions &&
								!permissions("admin")
							) {
								return null;
							}
						}
						return (
							<MenuItemLink
								key={resource.path || resource.name}
								to={to}
								isActive={(match, location) =>
									isResourceSelected(location, resource)
								}
								primaryText={
									resource.options && resource.options.title
										? resource.options.title
										: resource.options && resource.options.label
											? translate(resource.options.label)
											: translate(`menu.items.${resource.name}`)
								}
								leftIcon={
									<SafeBadge
										variant="dot"
										// invisible={get(badges, resource.name, 0) <= 0}
										invisible={
											get(
												(badges &&
													badges.find((b) => b.id === resource.name)) ||
												{},
												"count",
												0
											) <= 0
										}
										color="error"
									>
										{createElement(resource.icon)}
									</SafeBadge>
								}
								onClick={onMenuClick}
								button
							/>
						);
					})}
				</SubMenu>
			))}
			{loadedCms && !!pages && pages.length > 0 && (
				<SubMenu
					key="pages"
					handleToggle={() =>
						setVisibility({
							...visibility,
							pages: visibility.pages !== true,
						})
					}
					isOpen={visibility.pages || isGroupSelected(location, "pages")}
					sidebarIsOpen={open}
					name={`menu.groups.useful_links`}
					icon={<SubMenuIcon />}
				>
					{pages.map((item) => (
						<MenuItemLink
							key={`pages-${item.id}`}
							to={`/pages/${item.id}/show`}
							primaryText={item.name}
							isActive={(match, location) =>
								isResourceSelected(location, {
									name: `pages/${item.id}/show`,
									path: `/pages/${item.id}/show`,
								})
							}
							leftIcon={createElement(LinkIcon)}
							onClick={onMenuClick}
						/>
					))}
				</SubMenu>
			)}
			<Divider />
			<ContactMenu sidebarIsOpen={open} />
			<Responsive
				small={logout}
				medium={null} // Pass null to render nothing on larger devices
			/>
		</List>
	);
};

const mapStateToProps = (state) => ({
	open: state.admin.ui.sidebarOpen,
	resources: getResources(state),
});

export default translate(
	withRouter(connect(mapStateToProps)(withStyles(styles)(Menu)))
);
